import {defineStore} from 'pinia'
import {BoardsNs, ProjectNs, WorkspaceNs} from "@shared/types";
import {cookies, getBaseDomain, getLocalStorage, setLocalStorage} from "@shared/helpers";
import {PublicPageApi} from "@/api/public-page.apis.ts";
import {PublicNs} from "@shared/types/public.namespace.ts";


export const usePublicProjectStore = defineStore("publicProject", {
    state: () => ({
        token: '',
        password: '',
        publishInfo: {} as ProjectNs.PublishProjectResponse,
        workspaceData: {} as WorkspaceNs.Workspace,
        projectData: {} as ProjectNs.Project,
        recentlyViewed: [] as BoardsNs.RecentlyViewed[],
        isSidebarOpen: false,
        isSitemapOpen: false,
    }),
    getters: {
        getPublishInfo: (state) => state.publishInfo,
        getProjectData: (state) => state.projectData,
        getWorkspaceData: (state) => state.workspaceData,
        getProjectId: (state) => state.projectData.id,
        getProjectName: (state) => state.projectData.name,

        getRecentlyViewedByCurrentProject: (state) => {
            let rViewed = state.recentlyViewed;
            if (!rViewed || rViewed.length === 0) {
                rViewed = getLocalStorage('recentlyViewed');
            }
            if (rViewed && rViewed.length > 0 && state.projectData.id) {
                return rViewed.find((r) => r.projectId === state.projectData.id);
            }
            return {} as BoardsNs.RecentlyViewed;
        },
    },
    actions: {
        async checkToken(projectId: string, token: string): Promise<ProjectNs.PublishProjectResponse> {
            const payload = {projectId, token} as PublicNs.CheckTokenRequest;
            return PublicPageApi.checkToken(payload).then((publishInfo) => {
                this.publishInfo = publishInfo;
                return publishInfo;
            })
        },

        async loadProjectData(projectId: string, token: string, password: string): Promise<PublicNs.PublicProjectData> {
            const payload = {projectId, token, password} as PublicNs.LoadProjectDataRequest;
            return PublicPageApi.loadProjectData(payload).then((response) => {
                this.projectData = response.project;
                this.workspaceData = response.workspace;
                this.token = response.publish.token;
                this.password = password;

                if (password && password.length > 0) {
                    cookies.set(`pp-${projectId}`, password, {domain: getBaseDomain});
                }

                return response;
            })
        },

        addItemToRecentlyViewed(payload: { type: 'FEATURE' | 'SCREEN', item: any }): void {
            let rViewed = this.recentlyViewed;
            if (!rViewed || rViewed.length === 0) {
                rViewed = getLocalStorage('recentlyViewed');
                if (!rViewed) {
                    rViewed = [] as BoardsNs.RecentlyViewed[];
                }
            }

            let projectViewed;
            if (rViewed && rViewed.length > 0 && this.projectData.id) {
                projectViewed = rViewed.find((r) => r.projectId === this.projectData.id);
            }

            if (!projectViewed) {
                projectViewed = {
                    projectId: this.projectData.id,
                    features: [] as BoardsNs.RecentlyViewedItem[],
                    screens: [] as BoardsNs.RecentlyViewedItem[],
                } as BoardsNs.RecentlyViewed;
                rViewed.push(projectViewed);
            }

            if (payload.type === 'FEATURE') {
                const index = projectViewed.features.findIndex((r: BoardsNs.RecentlyViewedItem) => r.id === payload.item.id);
                if (index > -1) {
                    projectViewed.features.splice(index, 1);
                }
                projectViewed.features.unshift({
                    id: payload.item.id,
                    type: payload.item.featureType,
                    summary: payload.item.summary,
                    color: payload.item.properties?.color,
                });
                projectViewed.features = projectViewed.features.splice(0, 5);
            } else {
                const index = projectViewed.screens.findIndex((r: BoardsNs.RecentlyViewedItem) => r.id === payload.item.id);
                if (index > -1) {
                    projectViewed.screens.splice(index, 1);
                }
                projectViewed.screens.unshift({
                    id: payload.item.id,
                    type: payload.item.screenType,
                    summary: payload.item.summary,
                    color: payload.item.color,
                });
                projectViewed.screens = projectViewed.screens.splice(0, 5);
            }

            this.recentlyViewed = rViewed;
            setLocalStorage('recentlyViewed', rViewed);
        },
        toggleSidebar() {
            this.isSidebarOpen = !this.isSidebarOpen;
        },
        toggleSitemap() {
            this.isSitemapOpen = !this.isSitemapOpen;
        },
        closeSitemap() {
            this.isSitemapOpen = false;
        }
    }
})
